import { useEffect, useMemo, useState } from "react";
import { Loader2, Send, X, Plus, Minus, Trash2, MailCheck, Copy, Check, Copy as DuplicateIcon, Save, Truck, Calendar as CalendarIcon, Clock, UserCheck, ChevronDown } from "lucide-react";
import { format, parseISO } from "date-fns";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { api, formatApiErrorDetail } from "../lib/api";
import { classifyZip } from "../lib/deliveryZones";
import { US_STATES } from "../lib/usStates";
import { SuggestInput } from "./SuggestInput";
import { NewProductForm } from "./NewProductForm";
import { DeliveriesEditor, EMPTY_DELIVERY, dropFee } from "./DeliveriesEditor";

// Admin-facing modal for creating a self-pay ("pay-link") invoice.
// Backed by POST /api/admin/manual-invoices. Uses retail pricing (agency
// markup is intentionally NOT applied — see server.py `ManualInvoiceCreate`
// docstring for the business rule).
export const ManualInvoiceModal = ({ open, onClose, menu, onCreated, defaultContact = null }) => {
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [organization, setOrganization] = useState("");
  const [taxExempt, setTaxExempt] = useState(false);
  const [fulfillment, setFulfillment] = useState("delivery");
  const [zipCode, setZipCode] = useState("");
  // Dedicated delivery fee override. Auto-fills from the ZIP zone lookup
  // (same tier as the consumer cart) whenever the admin picks a
  // recognised ZIP, but the admin can override it — e.g. an off-tier
  // agency drop like JFK Airport that we quote a flat $25 for. Keeping
  // it as a separate field (rather than making the admin add "Local
  // Delivery Fee" as a line item) keeps the customer-facing invoice
  // rendering the fee in the totals block, not mixed in with the food.
  // Empty string = "use ZIP-derived default"; any numeric string = use
  // that explicit override.
  const [deliveryFeeOverride, setDeliveryFeeOverride] = useState("");
  const [address, setAddress] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [stateAbbr, setStateAbbr] = useState("NY");
  const [eventDate, setEventDate] = useState("");
  const [eventTime, setEventTime] = useState("");
  const [notes, setNotes] = useState("");
  const [customerNote, setCustomerNote] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [sendMode, setSendMode] = useState("now"); // "now" | "schedule" | "save"
  // sendNow retained as boolean for the payload; sendMode drives the UI.
  const [sendNow, setSendNow] = useState(true);
  const [items, setItems] = useState([]); // { package_id, quantity }
  const [pickerQuery, setPickerQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null); // { share_token, order, email_sent }
  const [linkCopied, setLinkCopied] = useState(false);
  // Approved-agencies dropdown used to link this invoice to an agency
  // account. When null, the invoice is billed to whoever the admin types
  // into the customer_name/email fields (with a "Create your account"
  // CTA baked into the email so they can self-serve later).
  const [assignedAgency, setAssignedAgency] = useState(null); // { id, email, name, organization }
  const [agencies, setAgencies] = useState([]);
  const [agencyQuery, setAgencyQuery] = useState("");
  const [agencyPickerOpen, setAgencyPickerOpen] = useState(false);
  // Requires-agency-approval flag: when true, the invoice lands in the
  // agency's queue as "pending_approval" and only becomes payable once
  // they tap Approve in their account. When false (default), it's
  // immediately payable via the standard pay-link flow.
  const [requireApproval, setRequireApproval] = useState(false);
  // Multi-drop invoice: several deliveries, each with its own date,
  // address, fee and items. `activeDrop` is where new items land.
  const [multiDrop, setMultiDrop] = useState(false);
  const [deliveries, setDeliveries] = useState([]);
  const [activeDrop, setActiveDrop] = useState(0);
  // Products created from inside this modal (incl. one-off items).
  const [localAdditions, setLocalAdditions] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      // Let inner dropdowns / date popovers close themselves first.
      if (e.key !== "Escape" || busy || e.defaultPrevented) return;
      if (document.querySelector("[data-radix-popper-content-wrapper]")) return;
      onClose();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, busy]);

  // Reset on open
  useEffect(() => {
    if (!open) return;
    // Pre-fill with the caller-supplied contact (e.g. the agency drill-down
    // "Create Invoice" button). Falls back to blank for the generic
    // Pay-Link workflow.
    setCustomerName(defaultContact?.name || "");
    setCustomerEmail(defaultContact?.email || "");
    setOrganization(defaultContact?.organization || "");
    setTaxExempt(!!defaultContact?.tax_exempt);
    setFulfillment("delivery"); setZipCode(""); setDeliveryFeeOverride("");
    setAddress(""); setAddressLine2(""); setCity(""); setStateAbbr("NY");
    setEventDate(""); setEventTime("");
    setNotes(""); setCustomerNote(""); setScheduledAt("");
    setSendMode("now"); setSendNow(true); setItems([]); setPickerQuery("");
    setError(""); setResult(null); setLinkCopied(false);
    // Preselect agency when the caller pre-fills a contact with user_id
    // (e.g., "Create Invoice" from the Agency drill-down).
    setAssignedAgency(
      defaultContact?.user_id
        ? {
            id: defaultContact.user_id,
            email: defaultContact.email,
            name: defaultContact.name || "",
            organization: defaultContact.organization || "",
          }
        : null
    );
    setAgencyQuery("");
    setAgencyPickerOpen(false);
    setRequireApproval(false);
    setMultiDrop(false); setDeliveries([]); setActiveDrop(0);
    setLocalAdditions([]); setCreateOpen(false);
    // Intentional: reset the form only when the modal opens, not while typing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Fetch approved agencies once for the picker.
  useEffect(() => {
    if (!open || agencies.length > 0) return;
    (async () => {
      try {
        const { data } = await api.get("/admin/agencies", { params: { status: "approved" } });
        setAgencies(data.items || []);
      } catch {
        // Non-fatal — the modal still works as a free-form Pay-Link.
        setAgencies([]);
      }
    })();
  }, [open, agencies.length]);

  const filteredAgencies = useMemo(() => {
    const q = agencyQuery.trim().toLowerCase();
    if (!q) return agencies.slice(0, 8);
    return agencies
      .filter((a) =>
        [a.email, a.name, a.organization]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().includes(q))
      )
      .slice(0, 12);
  }, [agencies, agencyQuery]);

  const pickAgency = (a) => {
    setAssignedAgency({
      id: a.id,
      email: a.email,
      name: a.name || "",
      organization: a.organization || "",
    });
    setCustomerName(a.name || "");
    setCustomerEmail(a.email || "");
    setOrganization(a.organization || "");
    setTaxExempt(a.tax_exempt_status === "approved");
    setAgencyPickerOpen(false);
    setAgencyQuery("");
  };

  const clearAgency = () => {
    setAssignedAgency(null);
    setRequireApproval(false);
  };

  // Type-ahead pick: fill the contact block, and link the agency account
  // when the suggestion is an approved agency user.
  const pickContact = (s) => {
    setCustomerName(s.name || "");
    setCustomerEmail(s.email || "");
    setOrganization(s.organization || "");
    if (s.tax_exempt) setTaxExempt(true);
    if (s.user_id) {
      setAssignedAgency({ id: s.user_id, email: s.email, name: s.name || "", organization: s.organization || "" });
    } else {
      clearAgency();
    }
  };
  const pickAddress = (s) => {
    setAddress(s.address || ""); setAddressLine2(s.address_line2 || "");
    setCity(s.city || ""); setStateAbbr(s.state || "NY"); setZipCode(s.zip_code || "");
  };

  const toggleMultiDrop = (on) => {
    setMultiDrop(on);
    setActiveDrop(0);
    if (on) {
      const first = { event_date: eventDate, event_time: eventTime, address, address_line2: addressLine2, city, state: stateAbbr, zip_code: zipCode, delivery_fee: deliveryFeeOverride };
      setDeliveries([first, { ...EMPTY_DELIVERY, event_date: eventDate }]);
      setItems((prev) => prev.map((it) => ({ ...it, delivery_index: 0 })));
    } else {
      setItems((prev) => prev.map(({ delivery_index, ...it }) => it));
    }
  };
  const setLineDrop = (idx, d) => setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, delivery_index: d } : it)));

  const allMenuItems = useMemo(() => [...localAdditions, ...(menu?.items || [])], [menu, localAdditions]);
  const menuItems = useMemo(
    () => [...localAdditions, ...(menu?.items || []).filter((m) => !m.one_off)],
    [menu, localAdditions]
  );
  const menuById = useMemo(() => Object.fromEntries(allMenuItems.map((m) => [m.id, m])), [allMenuItems]);
  const categories = useMemo(
    () => Array.from(new Set(menuItems.map((m) => m.category).filter(Boolean))).sort(),
    [menuItems]
  );
  const filteredMenu = useMemo(() => {
    const q = pickerQuery.trim().toLowerCase();
    if (!q) return menuItems.slice(0, 8);
    return menuItems
      .filter((m) => `${m.name} ${m.category || ""}`.toLowerCase().includes(q))
      .slice(0, 12);
  }, [menuItems, pickerQuery]);

  // Compute retail unit price for a menu row. The /api/menu endpoint now
  // returns retail for both anonymous and admin callers (only agency users
  // receive the +20% institutional markup), so the previous "back out the
  // admin markup" branch was dividing an already-retail price by 1.20 and
  // under-charging line items in the modal. Divide only when the caller
  // is agency; otherwise use the price as-is.
  const retailUnit = (pkg) => {
    if (menu?.role !== "agency") return Number(pkg.price || 0);
    const markup = (menu?.agency_markup_by_category?.[pkg.category]) || menu?.agency_markup || 1;
    return Number(pkg.price || 0) / (Number(markup) || 1);
  };

  // Options helpers — return the added modifier upcharge for a line.
  const optionUnitDelta = (pkg, selectedOptions) => {
    let delta = 0;
    for (const grp of pkg?.options || []) {
      const picked = selectedOptions?.[grp.label];
      const pickedList = Array.isArray(picked) ? picked : (picked ? [picked] : []);
      for (const v of grp.values || []) {
        const label = typeof v === "string" ? v : v.label;
        if (pickedList.includes(label)) {
          delta += typeof v === "string" ? 0 : (Number(v.price_delta) || 0);
        }
      }
    }
    return delta;
  };
  const lineUnitPrice = (line) => {
    const pkg = menuById[line.package_id];
    if (!pkg) return Number(line.unit_price) || 0;
    return Number(line.unit_price || 0) + optionUnitDelta(pkg, line.selected_options);
  };

  const addItem = (pkg) => {
    const retail = Math.round(retailUnit(pkg) * 100) / 100;
    setItems((prev) => [
      ...prev,
      { package_id: pkg.id, name: pkg.name, unit_price: retail, quantity: 1, selected_options: null, ...(multiDrop ? { delivery_index: activeDrop } : {}) },
    ]);
    setPickerQuery("");
  };
  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const setQty = (idx, q) => setItems((prev) => prev.map((p, i) => i === idx ? { ...p, quantity: Math.max(1, Math.min(999, Number(q) || 1)) } : p));
  const duplicateLine = (idx) => {
    setItems((prev) => {
      const src = prev[idx];
      if (!src) return prev;
      // Clone with a FRESH modifier selection so admin picks new options.
      const copy = { ...src, selected_options: null };
      const next = [...prev];
      next.splice(idx + 1, 0, copy);
      return next;
    });
  };
  const toggleOption = (idx, grpLabel, value, maxSelections = 1) => {
    setItems((prev) => prev.map((it, i) => {
      if (i !== idx) return it;
      const next = { ...(it.selected_options || {}) };
      const cur = Array.isArray(next[grpLabel]) ? next[grpLabel] : (next[grpLabel] ? [next[grpLabel]] : []);
      let updated;
      if (cur.includes(value)) {
        updated = cur.filter((v) => v !== value);
      } else if (maxSelections === 1) {
        updated = [value];
      } else {
        updated = [...cur, value].slice(-maxSelections);
      }
      if (updated.length === 0) delete next[grpLabel];
      else next[grpLabel] = maxSelections === 1 ? updated[0] : updated;
      return { ...it, selected_options: next };
    }));
  };

  // Live delivery fee. Uses the admin's explicit override when set,
  // otherwise falls back to the ZIP-derived default (same tier as the
  // consumer cart). Non-delivery orders always $0.
  const zone = useMemo(
    () => (fulfillment === "delivery" && zipCode ? classifyZip(zipCode) : null),
    [fulfillment, zipCode]
  );
  const deliveryFeeLive = multiDrop
    ? deliveries.reduce((s, d) => s + dropFee(d), 0)
    : fulfillment !== "delivery"
      ? 0
      : deliveryFeeOverride !== ""
      ? Math.max(0, Number(deliveryFeeOverride) || 0)
      : (zone?.fee ?? 0);
  const subtotal = items.reduce((s, i) => s + lineUnitPrice(i) * i.quantity, 0);
  const taxLive = taxExempt ? 0 : Math.round(subtotal * 0.08875 * 100) / 100;
  const totalLive = subtotal + deliveryFeeLive + taxLive;

  const dropsReady = !multiDrop || deliveries.every((_, i) => items.some((it) => it.delivery_index === i));
  const canSubmit = items.length > 0 && customerEmail.trim() && !busy && dropsReady;

  const lineDto = (i) => ({
    package_id: i.package_id,
    quantity: i.quantity,
    selected_options: i.selected_options || undefined,
  });
  const dropPayload = () => ({
    deliveries: deliveries.map((d, idx) => ({
      event_date: d.event_date || undefined,
      event_time: d.event_time || undefined,
      address: d.address.trim() || undefined,
      address_line2: d.address_line2.trim() || undefined,
      city: d.city.trim() || undefined,
      state: d.state || undefined,
      zip_code: d.zip_code || undefined,
      delivery_fee: d.delivery_fee !== "" ? Math.max(0, Number(d.delivery_fee) || 0) : undefined,
      items: items.filter((it) => it.delivery_index === idx).map(lineDto),
    })),
  });

  const submit = async () => {
    setError("");
    setBusy(true);
    try {
      // Convert local datetime (YYYY-MM-DDTHH:mm) → RFC3339 UTC for Resend.
      const scheduledIso = sendMode === "schedule" && scheduledAt
        ? new Date(scheduledAt).toISOString()
        : null;
      const payload = {
        ...(multiDrop ? dropPayload() : { items: items.map(lineDto) }),
        customer_email: customerEmail.trim(),
        customer_name: customerName.trim() || undefined,
        organization: organization.trim() || undefined,
        tax_exempt: taxExempt,
        fulfillment,
        zip_code: fulfillment === "delivery" ? zipCode.trim() || undefined : undefined,
        // Admin's explicit delivery fee override. When empty the backend
        // falls back to the ZIP-derived tier (existing behaviour).
        delivery_fee:
          fulfillment === "delivery" && deliveryFeeOverride !== ""
            ? Math.max(0, Number(deliveryFeeOverride) || 0)
            : undefined,
        address: fulfillment === "delivery" ? address.trim() || undefined : undefined,
        address_line2: fulfillment === "delivery" ? addressLine2.trim() || undefined : undefined,
        city: fulfillment === "delivery" ? city.trim() || undefined : undefined,
        state: fulfillment === "delivery" ? stateAbbr.trim() || undefined : undefined,
        event_date: eventDate || undefined,
        event_time: eventTime || undefined,
        notes: notes.trim() || undefined,
        customer_note: customerNote.trim() || undefined,
        send_now: sendMode !== "save",
        scheduled_at: scheduledIso || undefined,
        // When invoked from an agency drill-down OR when admin picks an
        // agency from the dropdown, tag the invoice with the agency's
        // user_id so it shows up in their /account/invoices.
        assign_user_id: assignedAgency?.id || undefined,
        // When true, the invoice status is set to "pending_approval" and
        // the agency must Approve it in their account before it's payable.
        require_agency_approval: !!(assignedAgency?.id && requireApproval),
      };
      const { data } = await api.post("/admin/manual-invoices", payload);
      setResult(data);
      onCreated?.(data);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || e?.message || "Could not create invoice");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!result?.share_token) return;
    const url = `${window.location.origin}/invoice/${result.share_token}`;
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      // no-op
    }
  };

  const sendEmailNow = async () => {
    if (!result?.order?.id || sending) return;
    setSending(true);
    setError("");
    try {
      const { data } = await api.post(`/admin/manual-invoices/${result.order.id}/resend`);
      setResult((prev) => (prev
        ? { ...prev, email_sent: true, order: { ...prev.order, invoice_sent_at: data?.invoice_sent_at || new Date().toISOString() } }
        : prev));
      onCreated?.(result);
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || e?.message || "Could not send email");
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="manual-invoice-modal"
      className="fixed inset-0 z-50 bg-brand-charcoal/70 backdrop-blur-sm flex items-center justify-center p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="w-full max-w-[640px] max-h-[90vh] overflow-y-auto rounded-3xl bg-white shadow-2xl">
        <div className="sticky top-0 z-10 bg-brand-charcoal text-white px-6 py-5">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-brand-gold">
                Pay-Link Invoice
              </p>
              <h3 className="font-display font-black text-[22px] tracking-tight mt-1">
                {result ? "Invoice created" : "Create & send"}
              </h3>
              {!result && (
                <p className="text-[12px] text-white/60 mt-1">
                  Retail pricing · Tax exempt toggle per invoice · Auto-creates account on pay
                </p>
              )}
            </div>
            <button
              type="button"
              data-testid="manual-invoice-close"
              onClick={onClose}
              disabled={busy}
              className="p-2 rounded-full text-white/70 hover:text-white hover:bg-white/10 disabled:opacity-40"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {result ? (
          <div className="p-6 space-y-5">
            <div
              className={`rounded-2xl px-4 py-4 flex items-start gap-3 ${
                result.email_sent
                  ? "bg-emerald-50 border border-emerald-200"
                  : "bg-brand-gold/10 border border-brand-gold/40"
              }`}
              data-testid={result.email_sent ? "manual-invoice-sent-banner" : "manual-invoice-saved-banner"}
            >
              <div
                className={`h-9 w-9 shrink-0 rounded-full flex items-center justify-center ${
                  result.email_sent ? "bg-emerald-600" : "bg-brand-gold-dark"
                }`}
              >
                <MailCheck className="h-4 w-4 text-white" />
              </div>
              <div className="flex-1">
                <p
                  className={`font-bold text-[13.5px] ${
                    result.email_sent ? "text-emerald-800" : "text-brand-charcoal"
                  }`}
                >
                  {result.scheduled
                    ? "Scheduled"
                    : result.email_sent
                    ? "Invoice sent"
                    : "Saved — not sent yet"}
                  {" · "}
                  {result.order?.invoice_number} · ${Number(result.order?.total || 0).toFixed(2)}
                </p>
                <p
                  className={`text-[12.5px] mt-0.5 ${
                    result.email_sent ? "text-emerald-800/85" : "text-brand-charcoal/75"
                  }`}
                >
                  {result.scheduled
                    ? `Will be emailed to ${result.order?.customer_email} on ${new Date(result.scheduled_at).toLocaleString()}.`
                    : result.email_sent
                    ? `Emailed to ${result.order?.customer_email} with a payment link.`
                    : "Copy the payment link to share manually — or send the email now."}
                </p>
              </div>
            </div>
            <div>
              <Label className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55">
                Public payment link
              </Label>
              <div className="mt-1.5 flex gap-2">
                <Input
                  data-testid="manual-invoice-share-url"
                  readOnly
                  value={`${window.location.origin}/invoice/${result.share_token}`}
                  className="h-11 rounded-xl bg-brand-cream/40 border-black/10 font-mono text-[12px]"
                />
                <button
                  type="button"
                  data-testid="manual-invoice-copy-link"
                  onClick={copyLink}
                  className={`inline-flex items-center gap-1.5 px-4 h-11 rounded-xl font-bold text-[11.5px] uppercase tracking-[0.1em] transition-colors ${
                    linkCopied ? "bg-emerald-600 text-white" : "bg-brand-charcoal text-white hover:bg-brand-charcoal/85"
                  }`}
                >
                  {linkCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {linkCopied ? "Copied" : "Copy"}
                </button>
              </div>
            </div>
            {!result.email_sent && result.order?.customer_email && (
              <button
                type="button"
                data-testid="manual-invoice-send-now"
                onClick={sendEmailNow}
                disabled={sending}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-teal text-white px-6 py-3 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal/90 transition-colors disabled:opacity-60"
              >
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {sending ? "Sending…" : "Send email now"}
              </button>
            )}
            {error && (
              <div
                data-testid="manual-invoice-error"
                role="alert"
                className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700"
              >
                {error}
              </div>
            )}
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-charcoal text-white px-6 py-3 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors"
            >
              Done
            </button>
          </div>
        ) : (
          <div className="p-6 space-y-5">
            {/* Agency selector — pre-fills the contact fields below and
                links the resulting invoice to an agency account so it
                shows up in their /account/invoices page. Leave blank for
                a one-off pay-link to an unrecognised buyer. */}
            <div data-testid="manual-invoice-agency-picker">
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55 mb-2">
                Agency Account (optional)
              </p>
              {assignedAgency ? (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-brand-teal/40 bg-brand-teal/8 p-3.5">
                  <div className="min-w-0 flex-1">
                    <p className="font-display font-black text-[16px] sm:text-[17px] text-brand-charcoal tracking-tight leading-tight truncate">
                      {assignedAgency.name || assignedAgency.email}
                    </p>
                    <p className="mt-0.5 text-[12px] text-brand-charcoal/60 truncate">
                      {assignedAgency.email}
                      {assignedAgency.organization && (
                        <> · <b className="text-brand-charcoal/80">{assignedAgency.organization}</b></>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <label
                      className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.08em] text-brand-charcoal cursor-pointer select-none"
                      title="If checked, invoice lands in the agency's account for approval before it becomes payable."
                    >
                      <input
                        type="checkbox"
                        data-testid="manual-invoice-require-approval"
                        checked={requireApproval}
                        onChange={(e) => setRequireApproval(e.target.checked)}
                        className="h-3.5 w-3.5"
                      />
                      Requires approval
                    </label>
                    <button
                      type="button"
                      data-testid="manual-invoice-agency-clear"
                      onClick={clearAgency}
                      className="rounded-full border border-black/10 bg-white text-brand-charcoal px-3 py-1.5 text-[10.5px] font-bold uppercase tracking-[0.1em] hover:border-red-300 hover:text-red-600"
                    >
                      Change
                    </button>
                  </div>
                </div>
              ) : (
                <div className="relative">
                  <button
                    type="button"
                    data-testid="manual-invoice-agency-open"
                    onClick={() => setAgencyPickerOpen((v) => !v)}
                    className="w-full flex items-center justify-between gap-3 rounded-xl border border-dashed border-brand-charcoal/25 bg-white px-3.5 py-3 text-[12.5px] font-bold text-brand-charcoal hover:border-brand-teal hover:text-brand-teal transition-colors"
                  >
                    <span className="inline-flex items-center gap-2">
                      <UserCheck className="h-4 w-4" />
                      Link to an approved agency…
                    </span>
                    <ChevronDown className="h-4 w-4 opacity-60" />
                  </button>
                  {agencyPickerOpen && (
                    <div className="absolute left-0 right-0 top-full mt-2 z-40 rounded-2xl border border-black/10 bg-white shadow-xl p-3 max-h-[280px] overflow-y-auto">
                      <input
                        type="text"
                        data-testid="manual-invoice-agency-search"
                        value={agencyQuery}
                        onChange={(e) => setAgencyQuery(e.target.value)}
                        placeholder="Search agencies by name, email, org…"
                        autoFocus
                        className="w-full h-10 rounded-lg bg-brand-cream/40 border border-black/10 px-3 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-brand-teal/40"
                      />
                      <ul className="mt-2 space-y-1">
                        {filteredAgencies.length === 0 && (
                          <li className="rounded-lg border border-dashed border-black/10 px-3 py-4 text-center text-[11.5px] text-brand-charcoal/55">
                            {agencies.length === 0
                              ? "No approved agencies yet."
                              : "No matches."}
                          </li>
                        )}
                        {filteredAgencies.map((a) => (
                          <li key={a.id}>
                            <button
                              type="button"
                              data-testid={`manual-invoice-agency-pick-${a.id}`}
                              onClick={() => pickAgency(a)}
                              className="w-full text-left rounded-lg px-3 py-2 hover:bg-brand-cream/40"
                            >
                              <p className="font-bold text-[13px] text-brand-charcoal truncate">
                                {a.name || "—"}
                              </p>
                              <p className="text-[11px] text-brand-charcoal/60 truncate">
                                {a.email}
                                {a.organization && ` · ${a.organization}`}
                              </p>
                            </button>
                          </li>
                        ))}
                      </ul>
                      <div className="mt-2 pt-2 border-t border-black/5">
                        <button
                          type="button"
                          data-testid="manual-invoice-agency-new"
                          onClick={() => setAgencyPickerOpen(false)}
                          className="w-full inline-flex items-center justify-center gap-1.5 rounded-lg bg-brand-charcoal text-white px-3 py-2 text-[10.5px] font-bold uppercase tracking-[0.1em] hover:bg-brand-teal"
                        >
                          <Plus className="h-3.5 w-3.5" />
                          New contact — fill in below
                        </button>
                        <p className="mt-1.5 text-[10.5px] text-brand-charcoal/50 text-center">
                          A &quot;Create your account&quot; button will be added to
                          their invoice email.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Customer */}
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55 mb-2">
                Customer
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <SuggestInput
                  testid="manual-invoice-email"
                  type="email"
                  placeholder="billing@agency.org *"
                  value={customerEmail}
                  onChange={setCustomerEmail}
                  onPick={pickContact}
                />
                <SuggestInput
                  testid="manual-invoice-name"
                  placeholder="Contact name"
                  value={customerName}
                  onChange={setCustomerName}
                  onPick={pickContact}
                />
                <SuggestInput
                  testid="manual-invoice-org"
                  placeholder="Organization / agency name"
                  value={organization}
                  onChange={setOrganization}
                  onPick={pickContact}
                  className="sm:col-span-2"
                />
              </div>
            </div>

            {/* Items picker */}
            <div>
              <div className="flex items-center justify-between gap-2 mb-2">
                <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55">
                  Items
                </p>
                <button
                  type="button"
                  data-testid="manual-invoice-new-product-toggle"
                  onClick={() => setCreateOpen((v) => !v)}
                  className="inline-flex items-center gap-1.5 rounded-full bg-brand-teal text-white px-3 py-1.5 text-[10.5px] font-bold uppercase tracking-[0.1em] hover:bg-brand-teal/85"
                >
                  <Plus className="h-3 w-3" />
                  {createOpen ? "Close" : "New product"}
                </button>
              </div>
              {multiDrop && (
                <div className="mb-2 flex flex-wrap items-center gap-1.5" data-testid="manual-invoice-active-drop">
                  <span className="text-[10.5px] font-bold uppercase tracking-[0.12em] text-brand-charcoal/55">Adding to:</span>
                  {deliveries.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      data-testid={`manual-invoice-active-drop-${i}`}
                      onClick={() => setActiveDrop(i)}
                      className={`rounded-full px-3 py-1 text-[10.5px] font-bold uppercase tracking-[0.1em] border ${
                        activeDrop === i ? "bg-brand-charcoal text-white border-brand-charcoal" : "bg-white text-brand-charcoal/70 border-black/10"
                      }`}
                    >
                      Delivery {i + 1}
                    </button>
                  ))}
                </div>
              )}
              {createOpen && (
                <NewProductForm
                  testidPrefix="manual-invoice-new-product"
                  categories={categories}
                  onCancel={() => setCreateOpen(false)}
                  onCreated={(item) => {
                    setLocalAdditions((prev) => [item, ...prev]);
                    addItem(item);
                    setCreateOpen(false);
                  }}
                />
              )}
              <div className="relative mt-2">
                <Input
                  data-testid="manual-invoice-item-search"
                  placeholder="Search menu (e.g. wings, coffee)…"
                  value={pickerQuery}
                  onChange={(e) => setPickerQuery(e.target.value)}
                  className="h-11 rounded-xl bg-brand-cream/40 border-black/10"
                />
                {pickerQuery && filteredMenu.length > 0 && (
                  <div className="absolute z-20 top-full mt-1 left-0 right-0 rounded-2xl bg-white shadow-lg border border-black/10 max-h-64 overflow-auto">
                    {filteredMenu.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        data-testid={`manual-invoice-pick-${m.id}`}
                        onClick={() => addItem(m)}
                        className="w-full text-left px-4 py-2.5 hover:bg-brand-cream/60 flex items-center gap-3"
                      >
                        <Plus className="h-3.5 w-3.5 text-brand-teal shrink-0" />
                        <span className="flex-1 min-w-0 truncate text-[13px] text-brand-charcoal">{m.name}</span>
                        {m.options?.length > 0 && (
                          <span className="text-[9px] font-bold uppercase tracking-[0.1em] text-brand-teal bg-brand-teal/10 rounded-full px-1.5 py-0.5">
                            has modifiers
                          </span>
                        )}
                        <span className="text-[12px] font-bold text-brand-charcoal/70 tabular-nums">${retailUnit(m).toFixed(2)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {items.length > 0 && (
                <ul className="mt-3 space-y-2">
                  {items.map((it, idx) => {
                    const pkg = menuById[it.package_id];
                    const hasOptions = pkg?.options?.length > 0;
                    const lineTotal = lineUnitPrice(it) * it.quantity;
                    return (
                      <li
                        key={`${it.package_id}-${idx}`}
                        data-testid={`manual-invoice-line-${it.package_id}-${idx}`}
                        className="rounded-xl bg-brand-cream/40 border border-black/5 p-3 space-y-2"
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-[13px] text-brand-charcoal truncate">{it.name}</p>
                            <p className="text-[11px] text-brand-charcoal/50 tabular-nums">
                              ${lineUnitPrice(it).toFixed(2)} ea
                            </p>
                            {multiDrop && (
                              <select
                                data-testid={`manual-invoice-line-drop-${idx}`}
                                value={it.delivery_index ?? 0}
                                onChange={(e) => setLineDrop(idx, Number(e.target.value))}
                                className="mt-1 h-7 rounded-full bg-white border border-black/10 px-2 text-[10.5px] font-bold uppercase tracking-[0.08em]"
                              >
                                {deliveries.map((_, i) => (
                                  <option key={i} value={i}>Delivery {i + 1}</option>
                                ))}
                              </select>
                            )}
                          </div>
                          <div className="flex items-center gap-1 rounded-full bg-white border border-black/10 px-1">
                            <button
                              type="button"
                              onClick={() => setQty(idx, it.quantity - 1)}
                              disabled={it.quantity <= 1}
                              className="p-1 rounded-full text-brand-charcoal/60 hover:text-brand-charcoal hover:bg-brand-cream disabled:opacity-30"
                              aria-label="Decrease"
                            >
                              <Minus className="h-3 w-3" />
                            </button>
                            <input
                              type="number"
                              data-testid={`manual-invoice-qty-${it.package_id}-${idx}`}
                              min={1} max={999}
                              value={it.quantity}
                              onChange={(e) => setQty(idx, e.target.value)}
                              className="w-10 h-6 text-center text-[13px] font-bold tabular-nums bg-transparent border-0 focus:outline-none"
                            />
                            <button
                              type="button"
                              onClick={() => setQty(idx, it.quantity + 1)}
                              className="p-1 rounded-full text-brand-charcoal/60 hover:text-brand-charcoal hover:bg-brand-cream"
                              aria-label="Increase"
                            >
                              <Plus className="h-3 w-3" />
                            </button>
                          </div>
                          <p className="w-20 text-right font-bold text-[13px] text-brand-charcoal tabular-nums">
                            ${lineTotal.toFixed(2)}
                          </p>
                          {hasOptions && (
                            <button
                              type="button"
                              data-testid={`manual-invoice-duplicate-${it.package_id}-${idx}`}
                              onClick={() => duplicateLine(idx)}
                              title="Build another pan with different modifiers"
                              aria-label="Duplicate line"
                              className="p-1.5 rounded-full text-brand-teal hover:bg-brand-teal/10"
                            >
                              <DuplicateIcon className="h-3.5 w-3.5" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => removeItem(idx)}
                            aria-label="Remove"
                            className="p-1.5 rounded-full text-brand-charcoal/50 hover:text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                        {/* Inline modifier picker — one pill row per option group */}
                        {hasOptions && (
                          <div className="pl-1 space-y-2">
                            {pkg.options.map((grp) => {
                              const picked = it.selected_options?.[grp.label];
                              const pickedList = Array.isArray(picked) ? picked : (picked ? [picked] : []);
                              const maxSel = Number(grp.max_selections || 1);
                              return (
                                <div key={grp.label}>
                                  <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-brand-charcoal/55 mb-1">
                                    {grp.label}{grp.required && " *"}
                                  </p>
                                  <div className="flex flex-wrap gap-1.5">
                                    {(grp.values || []).map((v, vIdx) => {
                                      const label = typeof v === "string" ? v : v.label;
                                      const delta = typeof v === "string" ? 0 : (Number(v.price_delta) || 0);
                                      const active = pickedList.includes(label);
                                      return (
                                        <button
                                          key={`${grp.label}-${vIdx}-${label}`}
                                          type="button"
                                          data-testid={`manual-invoice-opt-${it.package_id}-${idx}-${label}`.replace(/\s+/g, "-").toLowerCase()}
                                          onClick={() => toggleOption(idx, grp.label, label, maxSel)}
                                          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[10.5px] font-bold transition-colors border ${
                                            active
                                              ? "bg-brand-teal text-white border-brand-teal"
                                              : "bg-white text-brand-charcoal/70 border-black/10 hover:border-brand-teal/50"
                                          }`}
                                        >
                                          {label}
                                          {delta > 0 && (
                                            <span className={active ? "text-white/85" : "text-brand-gold"}>
                                              +${delta.toFixed(2)}
                                            </span>
                                          )}
                                        </button>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* Fulfillment picker + optional address (delivery only) */}
            <div>
              <Label className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55">Fulfillment</Label>
              {!multiDrop && (
              <div className="mt-1.5 flex gap-1 rounded-xl bg-brand-cream/40 border border-black/10 p-1 max-w-xs">
                {["delivery", "pickup"].map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    data-testid={`manual-invoice-fulfillment-${mode}`}
                    onClick={() => setFulfillment(mode)}
                    className={`flex-1 rounded-lg py-2 text-[11.5px] font-bold uppercase tracking-[0.1em] transition-colors ${
                      fulfillment === mode ? "bg-brand-charcoal text-white" : "text-brand-charcoal/60 hover:bg-white/60"
                    }`}
                  >
                    {mode}
                  </button>
                ))}
              </div>
              )}
              {(fulfillment === "delivery" || multiDrop) && (
                <label
                  data-testid="manual-invoice-multi-drop-toggle"
                  className="mt-2 flex items-center gap-2.5 rounded-xl border border-black/10 bg-white px-3.5 py-2.5 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={multiDrop}
                    onChange={(e) => toggleMultiDrop(e.target.checked)}
                    className="h-4 w-4 accent-brand-teal"
                  />
                  <span className="text-[12.5px] text-brand-charcoal">
                    <b>Multiple deliveries on one invoice</b>
                    <span className="block text-[11px] text-brand-charcoal/55">Each delivery gets its own date, address, items and fee.</span>
                  </span>
                </label>
              )}
              {multiDrop && (
                <div className="mt-3">
                  <DeliveriesEditor
                    deliveries={deliveries}
                    setDeliveries={setDeliveries}
                    items={items}
                    email={customerEmail}
                    onRemove={(idx) => {
                      setItems((prev) => prev.map((it) => (it.delivery_index >= idx ? { ...it, delivery_index: idx - 1 } : it)));
                      setActiveDrop((a) => Math.min(a, idx - 1));
                    }}
                  />
                  {!dropsReady && (
                    <p data-testid="manual-invoice-drops-warning" className="mt-2 text-[11px] font-bold text-brand-gold-dark">
                      Every delivery needs at least one item — pick a delivery under Items, then add products.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Full delivery address — pre-populates the customer's Pay-Link
                page. Optional at admin time; customer completes anything
                missing before checkout. */}
            {fulfillment === "delivery" && !multiDrop && (
              <div>
                <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55 mb-2">
                  <Truck className="h-3.5 w-3.5" />
                  Delivery address
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
                  <SuggestInput
                    kind="address"
                    email={customerEmail}
                    testid="manual-invoice-address"
                    placeholder="Street address"
                    value={address}
                    onChange={setAddress}
                    onPick={pickAddress}
                    className="sm:col-span-4"
                  />
                  <Input
                    data-testid="manual-invoice-address2"
                    placeholder="Apt / Suite / Floor"
                    value={addressLine2}
                    onChange={(e) => setAddressLine2(e.target.value)}
                    className="sm:col-span-2 h-11 rounded-xl bg-brand-cream/40 border-black/10"
                  />
                  <Input
                    data-testid="manual-invoice-city"
                    placeholder="City"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="sm:col-span-3 h-11 rounded-xl bg-brand-cream/40 border-black/10"
                  />
                  <select
                    data-testid="manual-invoice-state"
                    value={stateAbbr}
                    onChange={(e) => setStateAbbr(e.target.value)}
                    className="sm:col-span-1 h-11 rounded-xl bg-brand-cream/40 border border-black/10 px-3 text-[13px] focus:outline-none focus:ring-2 focus:ring-brand-teal"
                  >
                    {US_STATES.map(([code, name]) => (
                      <option key={code} value={code}>{code}</option>
                    ))}
                  </select>
                  <Input
                    data-testid="manual-invoice-zip"
                    placeholder="ZIP"
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value.replace(/\D/g, "").slice(0, 5))}
                    className="sm:col-span-2 h-11 rounded-xl bg-brand-cream/40 border-black/10"
                  />
                </div>
                {/* Dedicated delivery-fee override — lives beside ZIP so
                    it reads as "delivery info", never a food line item.
                    Auto-fills from the ZIP zone tier; type any $ amount
                    to override (e.g. custom-quoted flat fee for an
                    off-tier drop like JFK Airport). Empty = auto. */}
                <div className="mt-3">
                  <Label className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55">
                    Delivery Fee
                    {zone?.fee != null && deliveryFeeOverride === "" && (
                      <span className="ml-2 text-brand-teal/80 normal-case tracking-normal font-normal text-[10.5px]">
                        · auto ${zone.fee.toFixed(2)} from ZIP
                      </span>
                    )}
                    {deliveryFeeOverride !== "" && (
                      <span className="ml-2 text-brand-gold normal-case tracking-normal font-bold text-[10.5px]">
                        · manual override
                      </span>
                    )}
                  </Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-charcoal/50 text-[13px] font-bold">
                      $
                    </span>
                    <Input
                      data-testid="manual-invoice-delivery-fee"
                      type="number"
                      min="0"
                      step="0.01"
                      inputMode="decimal"
                      placeholder={
                        zone?.fee != null ? zone.fee.toFixed(2) : "0.00"
                      }
                      value={deliveryFeeOverride}
                      onChange={(e) => setDeliveryFeeOverride(e.target.value)}
                      className="h-11 rounded-xl bg-brand-cream/40 border-black/10 pl-7"
                    />
                    {deliveryFeeOverride !== "" && (
                      <button
                        type="button"
                        onClick={() => setDeliveryFeeOverride("")}
                        title="Reset to ZIP-based auto fee"
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold uppercase tracking-[0.1em] text-brand-charcoal/60 hover:text-brand-teal"
                      >
                        Reset
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Event date + time — customer can edit on the pay page, but
                pre-filling saves them a step and keeps the kitchen on the
                same schedule as the admin's booking notes. */}
            {!multiDrop && (
            <div>
              <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55 mb-2">
                <CalendarIcon className="h-3.5 w-3.5" />
                {fulfillment === "delivery" ? "Delivery date & time" : "Pickup date & time"}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55">
                    {fulfillment === "delivery" ? "Drop-off date" : "Pickup date"}
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        data-testid="manual-invoice-event-date"
                        className="mt-1.5 flex h-11 w-full items-center justify-between rounded-xl border border-black/10 bg-brand-cream/40 px-3 text-left text-[13px] hover:bg-brand-cream/70 focus:outline-none focus:ring-2 focus:ring-brand-teal"
                      >
                        <span className={eventDate ? "text-brand-charcoal" : "text-brand-charcoal/40"}>
                          {eventDate
                            ? format(parseISO(eventDate), "EEE, MMM d, yyyy")
                            : "Pick a date"}
                        </span>
                        <CalendarIcon className="h-4 w-4 text-brand-charcoal/50 shrink-0" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={eventDate ? parseISO(eventDate) : undefined}
                        onSelect={(d) => setEventDate(d ? format(d, "yyyy-MM-dd") : "")}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55">
                    {fulfillment === "delivery" ? "Drop-off time" : "Pickup time"}
                  </Label>
                  <Input
                    data-testid="manual-invoice-event-time"
                    type="time"
                    value={eventTime}
                    onChange={(e) => setEventTime(e.target.value)}
                    className="mt-1.5 h-11 rounded-xl bg-brand-cream/40 border-black/10"
                  />
                </div>
              </div>
              <p className="mt-2 text-[10.5px] text-brand-charcoal/50">
                Customer can still change these on the pay page before checkout.
              </p>
            </div>
            )}

            <label
              data-testid="manual-invoice-tax-exempt-toggle"
              className="flex items-center gap-3 rounded-xl bg-brand-gold/10 border border-brand-gold/30 px-4 py-3 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={taxExempt}
                onChange={(e) => setTaxExempt(e.target.checked)}
                className="h-4 w-4 accent-brand-gold"
              />
              <div className="flex-1">
                <p className="font-bold text-[13px] text-brand-charcoal">Tax exempt</p>
                <p className="text-[11.5px] text-brand-charcoal/60">
                  Skip sales tax on this invoice. Attach the tax-exempt doc to the account after payment.
                </p>
              </div>
            </label>

            <div>
              <Label className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55">Note to customer</Label>
              <Textarea
                data-testid="manual-invoice-customer-note"
                value={customerNote}
                onChange={(e) => setCustomerNote(e.target.value)}
                className="mt-1.5 rounded-xl bg-brand-cream/40 border-black/10"
                rows={2}
                maxLength={2000}
                placeholder="Shows on the pay-link page and in the email (e.g. 'Setup 8am, use loading dock')"
              />
            </div>

            <div>
              <Label className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55">Notes (internal)</Label>
              <Textarea
                data-testid="manual-invoice-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="mt-1.5 rounded-xl bg-brand-cream/40 border-black/10"
                rows={2}
                placeholder="Optional context for the invoice (not shown to customer)"
              />
            </div>

            {/* Preview — live subtotal, delivery (from ZIP), tax, total */}
            <div className="rounded-2xl bg-brand-charcoal text-white px-4 py-3 space-y-1.5">
              <div className="flex items-center justify-between text-[13px]">
                <span className="text-white/60">Subtotal</span>
                <span className="tabular-nums font-bold">${subtotal.toFixed(2)}</span>
              </div>
              {(fulfillment === "delivery" || multiDrop) && (
                <div className="flex items-center justify-between text-[13px]">
                  <span className="text-white/60 flex items-center gap-1.5">
                    <Truck className="h-3 w-3" />
                    {multiDrop ? `Delivery × ${deliveries.length}` : "Delivery"}
                    {!multiDrop && zone && zone.fee != null && (
                      <span className="text-[10px] text-brand-gold uppercase tracking-[0.1em]">
                        {zone.tier}
                      </span>
                    )}
                  </span>
                  <span className="tabular-nums font-bold" data-testid="manual-invoice-delivery-live">
                    ${deliveryFeeLive.toFixed(2)}
                  </span>
                </div>
              )}
              {fulfillment === "delivery" && !multiDrop && zipCode && !zone?.fee && deliveryFeeOverride === "" && (
                <p className="text-[10.5px] text-brand-gold">
                  ZIP {zipCode} is outside flat-rate zones — customer must request a custom quote.
                </p>
              )}
              <div className="flex items-center justify-between text-[13px]">
                <span className="text-white/60">
                  Tax {taxExempt && <span className="ml-1 text-[10px] uppercase font-bold text-brand-gold">exempt</span>}
                </span>
                <span className="tabular-nums font-bold">${taxLive.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between text-[15px] pt-1.5 mt-1 border-t border-white/10">
                <span className="text-white/80 font-bold">Total</span>
                <span className="tabular-nums font-black text-brand-teal" data-testid="manual-invoice-total-live">
                  ${totalLive.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Send mode picker — three-way choice: send now / schedule / save
                only (no email). The schedule option surfaces a datetime-local
                picker; the ISO value is passed to Resend as `scheduled_at`. */}
            <div>
              <Label className="text-[11px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55">
                When should we send this?
              </Label>
              <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-3 gap-2">
                {[
                  { id: "now", label: "Send now", icon: Send, hint: "Email the link immediately" },
                  { id: "schedule", label: "Schedule", icon: Clock, hint: "Email at a future date & time" },
                  { id: "save", label: "Save only", icon: Save, hint: "Don't email — you'll copy the link" },
                ].map(({ id, label, icon: Icon, hint }) => {
                  const active = sendMode === id;
                  return (
                    <button
                      key={id}
                      type="button"
                      data-testid={`manual-invoice-mode-${id}`}
                      onClick={() => setSendMode(id)}
                      className={`text-left rounded-2xl border p-3 transition-colors ${
                        active
                          ? "border-brand-teal bg-brand-teal/5"
                          : "border-black/10 hover:border-brand-teal/40"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className="h-3.5 w-3.5" />
                        <span className="text-[12px] font-bold uppercase tracking-[0.1em] text-brand-charcoal">{label}</span>
                      </div>
                      <p className="mt-1 text-[11px] text-brand-charcoal/55">{hint}</p>
                    </button>
                  );
                })}
              </div>
              {sendMode === "schedule" && (
                <div className="mt-3">
                  <Label className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-brand-charcoal/55">
                    Send at
                  </Label>
                  <Input
                    data-testid="manual-invoice-schedule-at"
                    type="datetime-local"
                    value={scheduledAt}
                    min={new Date(Date.now() + 5 * 60 * 1000).toISOString().slice(0, 16)}
                    onChange={(e) => setScheduledAt(e.target.value)}
                    className="mt-1.5 h-11 rounded-xl bg-brand-cream/40 border-black/10"
                  />
                  <p className="mt-1.5 text-[10.5px] text-brand-charcoal/50">
                    Delivered via Resend at this local time.
                  </p>
                </div>
              )}
            </div>

            {error && (
              <div
                data-testid="manual-invoice-error"
                role="alert"
                className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-[13px] text-red-700"
              >
                {error}
              </div>
            )}

            {/* Single primary submit button — driven by the sendMode picker
                above so the admin's intent is explicit. */}
            <div className="pt-1">
              <button
                type="button"
                data-testid="manual-invoice-submit"
                onClick={submit}
                disabled={!canSubmit || (sendMode === "schedule" && !scheduledAt)}
                title={
                  sendMode === "schedule"
                    ? "Save the invoice and schedule the email for the picked date/time."
                    : sendMode === "save"
                    ? "Save the invoice without emailing. Copy the pay-link on the next screen."
                    : "Save the invoice and email the pay-link now."
                }
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-charcoal text-white px-6 py-3 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {busy ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : sendMode === "schedule" ? (
                  <Clock className="h-4 w-4" />
                ) : sendMode === "save" ? (
                  <Save className="h-4 w-4" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {sendMode === "schedule"
                  ? "Schedule send"
                  : sendMode === "save"
                  ? "Save (don't send)"
                  : "Save & email now"}
              </button>
            </div>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-transparent text-brand-charcoal/50 px-5 py-2 text-[11px] font-bold uppercase tracking-[0.12em] hover:text-brand-charcoal disabled:opacity-60"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
